import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Mail, Copy, Check, RefreshCw, AlertTriangle } from 'lucide-react';

const ClientAccessModal = ({
  isOpen,
  accessCode,
  contract,
  portalUrl,
  onClose,
  onCopy,
  onSendEmail,
  onRegenerate,
  isSendingEmail = false,
  emailSent = false,
  isExistingCode = false
}) => {
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false);

  if (!isOpen) return null;

  const handleCopyDetails = () => {
    const text = `Your Client Portal Access:\n\nURL: ${portalUrl}/client-portal\nEmail: ${contract?.client_email}\nAccess Code: ${accessCode}`;
    navigator.clipboard.writeText(text);
    onCopy();
  };

  const handleRegenerate = () => {
    setShowRegenerateConfirm(false);
    if (onRegenerate) {
      onRegenerate(contract);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="bg-gradient-to-r from-[#1e3a5f] to-[#2d5016] text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Mail size={20} />
            Client Portal Access Code
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <p className="text-sm text-gray-600">
            Share this access code with your client so they can view and sign their contract.
          </p>
          
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 p-4 rounded-lg text-center border-2 border-dashed border-[#1e3a5f]">
            <p className="text-sm text-gray-500 mb-1">Access Code</p>
            <p className="text-3xl font-mono font-bold tracking-wider text-[#1e3a5f]">
              {accessCode}
            </p>
          </div>
          
          <div className="bg-blue-50 p-3 rounded-lg text-sm">
            <p className="font-medium text-blue-800 mb-1">Client Portal URL:</p>
            <p className="text-blue-600 break-all font-mono text-xs">
              {portalUrl}/client-portal
            </p>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <p className="text-xs text-gray-500">Client:</p>
            <p className="font-medium text-[#1e3a5f]">{contract?.client_name}</p>
            <p className="text-sm text-gray-600">{contract?.client_email}</p>
          </div>

          {emailSent && (
            <div className="bg-green-50 p-3 rounded-lg flex items-center gap-2 text-green-700">
              <Check size={18} />
              <span className="text-sm font-medium">Email sent successfully!</span>
            </div>
          )}

          {/* Regenerate Confirmation */}
          {showRegenerateConfirm && (
            <div className="bg-amber-50 border border-amber-200 p-3 rounded-lg">
              <div className="flex items-start gap-2 text-amber-700 mb-3">
                <AlertTriangle size={18} className="mt-0.5" />
                <div>
                  <p className="font-medium text-sm">Generate new code?</p>
                  <p className="text-xs">This will invalidate the current access code.</p>
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowRegenerateConfirm(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleRegenerate}
                  className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                >
                  Regenerate
                </Button>
              </div>
            </div>
          )}
          
          <div className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={onClose}
              className="flex-1"
            >
              Close
            </Button>
            {onSendEmail && (
              <Button
                onClick={() => onSendEmail(contract, accessCode)}
                disabled={isSendingEmail || emailSent}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white"
              >
                {isSendingEmail ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Sending...
                  </>
                ) : emailSent ? (
                  <>
                    <Check size={16} className="mr-2" />
                    Sent
                  </>
                ) : (
                  <>
                    <Mail size={16} className="mr-2" />
                    Send Email
                  </>
                )}
              </Button>
            )}
            <Button
              onClick={handleCopyDetails}
              className="flex-1 bg-[#1e3a5f] hover:bg-[#15422a]"
            >
              <Copy size={16} className="mr-2" />
              Copy
            </Button>
          </div>

          {/* Regenerate button at bottom */}
          {onRegenerate && !showRegenerateConfirm && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowRegenerateConfirm(true)}
              className="w-full text-gray-500 hover:text-gray-700"
            >
              <RefreshCw size={14} className="mr-2" />
              Generate new code (invalidates current)
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientAccessModal;
